/**
 * Reimbursement Internal Notes service — mirrors Angular "frequently-used-notes" endpoints.
 * List loads the full config once; search/pagination/reorder-preview are client-side.
 */

import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type { ReimbursementNote, ReimbursementNotePayload } from '../types'

const NOTES_API_BASE = '/api/frequently-used-notes'

/** Backend returns either a bare array or `{ data: [...] }`. */
function normalizeList(data: unknown): ReimbursementNote[] {
  if (Array.isArray(data)) return data as ReimbursementNote[]
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: ReimbursementNote[] }).data
  }
  return []
}

export async function getReimbursementNotes(): Promise<ReimbursementNote[]> {
  try {
    const response = await apiClient.get(`${NOTES_API_BASE}/config`)
    return normalizeList(response.data)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load notes.')
  }
}

export async function createReimbursementNote(payload: ReimbursementNotePayload): Promise<void> {
  try {
    await apiClient.post(NOTES_API_BASE, payload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save note.')
  }
}

export async function updateReimbursementNote(
  id: number,
  payload: ReimbursementNotePayload,
): Promise<void> {
  try {
    await apiClient.put(`${NOTES_API_BASE}/${id}`, payload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save note.')
  }
}

export async function deleteReimbursementNote(id: number): Promise<void> {
  try {
    await apiClient.delete(`${NOTES_API_BASE}/${id}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to remove message.')
  }
}

export async function reorderReimbursementNotes(order: number[]): Promise<void> {
  try {
    await apiClient.put(`${NOTES_API_BASE}/reorder`, { order })
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update order.')
  }
}
